import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useQuiz } from '../../../../contexts/QuizContext';

const SectionItem = ({
  section,
  index,
  activeSection,
  setActiveSection,
  sectionDataMap = {},
  isSectionVisible = true,
}) => {
  const { control } = useQuiz();
  const watchedValues =
    useWatch({
      control,
      name: index === activeSection ? undefined : '__noop__',
    }) || {};

  const shouldRenderSection = useMemo(() => {
    if (!isSectionVisible) return false;
    if (!sectionDataMap || Object.keys(sectionDataMap).length === 0) {
      return true;
    }
    let currentSectionData = null;
    const sectionId =
      section.id || section.sectionId || section.metadata?.sectionId;
    if (sectionId && sectionDataMap[sectionId]) {
      currentSectionData = sectionDataMap[sectionId];
    }

    if (!currentSectionData) {
      return false;
    }

    const { questions = [], existingAnswers = [] } = currentSectionData;

    return questions.length > 0 || existingAnswers.length > 0;
  }, [section, sectionDataMap, isSectionVisible]);

  // Compute progress using normalized question data
  // Moved this useMemo before the conditional return to fix the React Hook rule violation
  const { requiredCount, answeredCount, percent } = useMemo(() => {
    const questions = Array.isArray(section?.questions)
      ? section.questions
      : [];
    let req = 0;
    let ans = 0;
    for (const q of questions) {
      const isHidden = Boolean(q?.isHidden ?? q?.metadata?.hidden);
      const isRequired = Boolean(
        q?.required ?? q?.isRequired ?? q?.metadata?.required
      );
      const optedOut = Boolean(q?.optedOut);
      if (isHidden) continue;
      if (!isRequired || optedOut) continue;
      req += 1;
      // Choose live value for active section; otherwise fallback to persisted answer
      const v =
        index === activeSection && q?.questionId
          ? watchedValues[q.questionId]
          : q?.answer;
      let hasVal = false;
      if (v == null) {
        hasVal = false;
      } else if (Array.isArray(v)) {
        hasVal = v.length > 0;
      } else if (typeof v === 'string') {
        hasVal = v.trim().length > 0;
      } else {
        // numbers/booleans/objects (e.g., date strings already handled above)
        hasVal = true;
      }
      if (hasVal) ans += 1;
    }
    const pct = req > 0 ? Math.round((ans / req) * 100) : 0;
    return { requiredCount: req, answeredCount: ans, percent: pct };
  }, [section, watchedValues, index, activeSection]);

  if (!shouldRenderSection) {
    return null;
  }

  return (
    <button
      key={section.id}
      onClick={() => setActiveSection(index)}
      className={`w-full text-left p-3 rounded-lg border transition-colors ${
        activeSection === index
          ? 'border-violet-200 bg-violet-50 dark:border-violet-700 dark:bg-violet-900/20'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex-1 min-w-0">
          <span
            className={`text-sm font-medium truncate block ${
              activeSection === index
                ? 'text-violet-700 dark:text-violet-300'
                : 'text-gray-700 dark:text-gray-300'
            }`}
          >
            {section.title}
          </span>
          {section.description && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
              {section.description}
            </div>
          )}
        </div>
      </div>

      <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
        <div
          className={`h-2 rounded-full transition-all duration-300 ${
            percent === 100
              ? 'bg-green-500'
              : percent > 0
                ? 'bg-violet-500'
                : 'bg-gray-300'
          }`}
          style={{ width: `${percent}%` }}
        ></div>
      </div>

      <div className="flex items-center justify-between mt-1">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {percent}% complete
          {requiredCount > 0
            ? ` • ${answeredCount}/${requiredCount} required`
            : ''}
        </div>
      </div>
    </button>
  );
};

SectionItem.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    progress: PropTypes.number,
    questions: PropTypes.arrayOf(
      PropTypes.shape({
        questionId: PropTypes.string,
        required: PropTypes.bool,
        isRequired: PropTypes.bool,
        isHidden: PropTypes.bool,
        optedOut: PropTypes.bool,
        answer: PropTypes.any,
        metadata: PropTypes.object,
      })
    ),
  }).isRequired,
  index: PropTypes.number.isRequired,
  activeSection: PropTypes.number.isRequired,
  setActiveSection: PropTypes.func.isRequired,
  sectionDataMap: PropTypes.object,
  isSectionVisible: PropTypes.bool,
};

SectionItem.defaultProps = {
  sectionDataMap: {},
  isSectionVisible: true,
};

export default SectionItem;
